import { DatabaseModel } from './database';
import type { RootKey } from './types';
import type { Workspace } from './workspace';

/**
 * Universe: the graph of a workspace's pages, like Obsidian's graph view. Nodes are pages (and optionally
 * database rows); edges are links between pages (mentions, link-to-page blocks, internal links), and optionally
 * the page hierarchy and database relations. Pure: the app lays it out and draws it (`packages/app/src/universe`).
 */

export type UniverseEdgeKind = 'link' | 'child' | 'relation';

export interface UniverseNode {
  id: string;
  title: string;
  icon: string | null;
  kind: 'page' | 'database' | 'row';
  /** Root area: `private:<user>` or `team:<id>`. */
  group: string;
  parentId: string | null;
  /** Depth in the page tree (top-level pages are 0). */
  depth: number;
  /** Number of edges touching the node (all kinds shown). */
  degree: number;
  /** Links from and to this page (link edges only). */
  links: number;
  backlinks: number;
  createdAt: number;
  editedAt: number;
}

export interface UniverseEdge {
  source: string;
  target: string;
  kind: UniverseEdgeKind;
}

export interface UniverseGroup {
  key: string;
  label: string;
  /** Stable palette index. */
  index: number;
}

export interface UniverseOptions {
  /** Parent → subpage edges (default true). */
  hierarchy?: boolean;
  /** Database rows as nodes (default false), with their relations. */
  rows?: boolean;
  /** Pages without any edge (default true). */
  orphans?: boolean;
  /** Only the neighbourhood of a page, up to `depth` steps (local graph). */
  focus?: { id: string; depth: number } | null;
  /** The viewer: only their private pages are included (others' private pages are not theirs to see). */
  me?: string;
  /** Ids to leave out (e.g. pages the user cannot open). */
  hidden?: (id: string) => boolean;
}

export interface Universe {
  nodes: UniverseNode[];
  edges: UniverseEdge[];
  groups: UniverseGroup[];
}

export function buildUniverse(ws: Workspace, opts: UniverseOptions = {}): Universe {
  const hierarchy = opts.hierarchy ?? true;
  const rows = opts.rows ?? false;
  const orphans = opts.orphans ?? true;

  // Pages in the tree, top-down, so each knows its depth and root area.
  const nodes = new Map<string, UniverseNode>();
  const groups = new Map<string, UniverseGroup>();
  const groupLabel = (key: RootKey): string => {
    if (key.startsWith('team:')) return ws.getTeamspace(key.slice(5))?.name ?? 'Teamspace';
    return 'Private';
  };
  const visit = (id: string, group: RootKey, depth: number, parentId: string | null, inRows: boolean) => {
    const p = ws.getPage(id);
    if (!p || p.trashed || p.isTemplate || nodes.has(id) || opts.hidden?.(id)) return;
    const kind = inRows ? 'row' : p.kind === 'database' ? 'database' : 'page';
    if (kind === 'row' && !rows) return;
    nodes.set(id, { id, title: p.title.trim() || 'Untitled', icon: p.icon, kind, group, parentId, depth, degree: 0, links: 0, backlinks: 0, createdAt: p.createdAt, editedAt: p.editedAt });
    if (!groups.has(group)) groups.set(group, { key: group, label: groupLabel(group), index: groups.size });
    for (const c of ws.childIds(id)) visit(c, group, depth + 1, id, p.kind === 'database');
  };
  const roots: RootKey[] = [];
  for (const t of ws.listTeamspaces()) if (!t.archived) roots.push(`team:${t.id}`);
  if (opts.me) roots.unshift(`private:${opts.me}`);
  else for (const p of ws.allPages()) if (p.parent.type === 'workspace' && !roots.includes(`private:${p.parent.id}`)) roots.push(`private:${p.parent.id}`);
  for (const key of roots) for (const id of ws.rootIds(key)) visit(id, key, 0, null, false);

  const edges: UniverseEdge[] = [];
  const seen = new Set<string>();
  const add = (source: string, target: string, kind: UniverseEdgeKind) => {
    if (source === target || !nodes.has(source) || !nodes.has(target)) return;
    // One edge per pair and kind; links count both directions once.
    const key = kind === 'child' ? `${kind}:${source}:${target}` : `${kind}:${[source, target].sort().join(':')}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ source, target, kind });
  };
  for (const n of nodes.values()) {
    for (const t of ws.linksOf(n.id)) {
      if (!nodes.has(t) || t === n.id) continue;
      n.links++;
      nodes.get(t)!.backlinks++;
      add(n.id, t, 'link');
    }
    if (hierarchy && n.parentId) add(n.parentId, n.id, 'child');
  }
  if (rows) {
    const model = new DatabaseModel(ws);
    for (const dbId of model.allDatabases()) {
      const relations = model.schema(dbId).filter((p) => p.type === 'relation');
      if (!relations.length) continue;
      for (const row of model.rows(dbId)) for (const prop of relations) for (const t of (row.props[prop.id] as string[] | undefined) ?? []) add(row.id, t, 'relation');
    }
  }

  let keep = new Set(nodes.keys());
  if (opts.focus && nodes.has(opts.focus.id)) {
    const adj = new Map<string, string[]>();
    for (const e of edges) {
      (adj.get(e.source) ?? adj.set(e.source, []).get(e.source)!).push(e.target);
      (adj.get(e.target) ?? adj.set(e.target, []).get(e.target)!).push(e.source);
    }
    keep = new Set([opts.focus.id]);
    let frontier = [opts.focus.id];
    for (let d = 0; d < opts.focus.depth; d++) {
      const next: string[] = [];
      for (const id of frontier)
        for (const m of adj.get(id) ?? []) {
          if (keep.has(m)) continue;
          keep.add(m);
          next.push(m);
        }
      frontier = next;
    }
  }
  const finalEdges = edges.filter((e) => keep.has(e.source) && keep.has(e.target));
  for (const e of finalEdges) {
    nodes.get(e.source)!.degree++;
    nodes.get(e.target)!.degree++;
  }
  const finalNodes = [...nodes.values()].filter((n) => keep.has(n.id) && (orphans || n.degree > 0 || n.id === opts.focus?.id));
  const used = new Set(finalNodes.map((n) => n.group));
  return { nodes: finalNodes, edges: finalEdges, groups: [...groups.values()].filter((g) => used.has(g.key)) };
}

/** Case-insensitive title match for the Universe search box (every word must appear). */
export function universeMatches(node: Pick<UniverseNode, 'title'>, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const title = node.title.toLowerCase();
  return words.every((w) => title.includes(w));
}
